import { useAuthStore } from '@/stores/useAuthStore';
import { useUIStore } from '@/stores/useUIStore';
import { isStaffRole } from '@/types';
import type { UUID } from '@/types';
import { useSearchParams } from 'react-router-dom';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Drops sentinels like `unassigned` and other non-UUID query values. */
export function parseUuid(value: string | null | undefined): UUID | undefined {
  if (!value || !UUID_RE.test(value)) return undefined;
  return value as UUID;
}

/** Staff tenant query. Ignores sentinels like `unassigned` used as list filters. */
export function parseStaffClienteId(value: string | null | undefined): UUID | undefined {
  return parseUuid(value);
}

/**
 * The tenant whose data the current view should show.
 *
 * Tenant users always get `undefined`: RLS scopes their queries from the JWT,
 * and sending an explicit `clienteId` would be both redundant and a way to
 * accidentally leak another tenant's identifier into a URL. Staff, who see all
 * tenants, prefer `?clienteId=` (shareable) and fall back to the switcher store.
 */
export function useTenantScope(): UUID | undefined {
  const isStaff = useAuthStore((state) => {
    const role = state.user?.ruolo;
    return isStaffRole(role);
  });
  const impersonated = useUIStore((state) => state.impersonatedClienteId);
  const [searchParams] = useSearchParams();

  if (!isStaff) return undefined;
  return parseStaffClienteId(searchParams.get('clienteId')) ?? impersonated ?? undefined;
}
